import React, { useState, useEffect } from 'react'
import {
  BellIcon,
  CheckCircleIcon,
  ClockIcon,
  SunIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'
import api from '../utils/api'
import { AlertModal } from '../components/ConfirmModal'

interface Notification {
  id: number
  type: string
  message: string
  read: boolean
  created_at: string
}

export const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean
    title?: string
    message: string
    type?: 'success' | 'error' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Reminder settings
  const [morningTime, setMorningTime] = useState('07:30')
  const [morningEnabled, setMorningEnabled] = useState(true)
  const [lateTime, setLateTime] = useState('09:20')
  const [lateEnabled, setLateEnabled] = useState(true)
  const [exitTime, setExitTime] = useState('17:15')
  const [exitEnabled, setExitEnabled] = useState(true)
  const [reportEnabled, setReportEnabled] = useState(true)

  const fetchData = async () => {
    try {
      const [notifsRes, meRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/auth/me')
      ])
      
      if (Array.isArray(notifsRes.data)) {
        setNotifications(notifsRes.data)
      } else {
        setNotifications([])
      }

      if (meRes.data) {
        setMorningTime(meRes.data.remind_morning_time || '07:30')
        setMorningEnabled(meRes.data.remind_morning_enabled ?? true)
        setLateTime(meRes.data.remind_late_time || '09:20')
        setLateEnabled(meRes.data.remind_late_enabled ?? true)
        setExitTime(meRes.data.remind_exit_time || '17:15')
        setExitEnabled(meRes.data.remind_exit_enabled ?? true)
        setReportEnabled(meRes.data.remind_report_enabled ?? true)
      }
    } catch (err) {
      console.error(err)
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleSaveSettings = async () => {
    setSettingsLoading(true)
    setSavedSuccess(false)
    try {
      await api.patch('/auth/me', {
        remind_morning_time: morningTime,
        remind_morning_enabled: morningEnabled,
        remind_late_time: lateTime,
        remind_late_enabled: lateEnabled,
        remind_exit_time: exitTime,
        remind_exit_enabled: exitEnabled,
        remind_report_enabled: reportEnabled
      })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
      setAlertDialog({
        isOpen: true,
        type: 'success',
        title: 'ذخیره شد',
        message: 'تنظیمات زمان‌بندی یادآورهای تلگرام با موفقیت ثبت گردید.'
      })
    } catch (err: any) {
      setAlertDialog({
        isOpen: true,
        type: 'error',
        title: 'خطا',
        message: err.response?.data?.detail || 'خطا در ذخیره تنظیمات یادآورها'
      })
    } finally {
      setSettingsLoading(false)
    }
  }

  const handleMarkAsRead = async (id: number) => {
    try {
      await api.post(`/notifications/${id}/read`)
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true } : n))
      )
    } catch (err) {
      console.error(err)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    } catch (err) {
      console.error(err)
    }
  }

  const hoursOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const minsOptions = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55']

  const renderTimePicker = (val: string, setVal: (v: string) => void) => {
    const [h, m] = (val || '09:00').split(':')
    return (
      <div className="flex items-center gap-1">
        <select
          value={h}
          onChange={e => setVal(`${e.target.value}:${m || '00'}`)}
          className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1 text-xs font-bold text-slate-800 dark:text-slate-100"
        >
          {hoursOptions.map(opt => <option key={opt} value={opt} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{opt}</option>)}
        </select>
        <span>:</span>
        <select
          value={m}
          onChange={e => setVal(`${h || '09'}:${e.target.value}`)}
          className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-2 py-1 text-xs font-bold text-slate-800 dark:text-slate-100"
        >
          {minsOptions.map(opt => <option key={opt} value={opt} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100">{opt}</option>)}
        </select>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-20 max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-2xl">
            <BellIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white">
              اعلانات و زمان‌بندی یادآورها
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              تنظیم ساعات پیام‌های خودکار ربات تلگرام و تاریخچه پیام‌ها
            </p>
          </div>
        </div>

        <button
          onClick={handleMarkAllRead}
          className="text-xs px-3.5 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-semibold rounded-xl transition"
        >
          خواندن همه
        </button>
      </div>

      {/* Reminder Schedule Settings Card */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-700">
          <h2 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-emerald-600" />
            تنظیم زمان ارسال یادآورهای ربات تلگرام
          </h2>
          <button
            onClick={handleSaveSettings}
            disabled={settingsLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-sm disabled:opacity-50"
          >
            {savedSuccess ? (
              <>
                <CheckIcon className="w-4 h-4" />
                ذخیره شد
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-4 h-4" />
                {settingsLoading ? 'در حال ذخیره...' : 'ذخیره زمان‌بندی'}
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Morning */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60">
            <div className="space-y-0.5">
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <SunIcon className="w-4 h-4 text-amber-500" />
                یادآوری صبحگاهی ورود
              </span>
              <p className="text-[11px] text-slate-400">ارسال پیام صبح بخیر و ثبت ورود</p>
            </div>
            <div className="flex items-center gap-3">
              {renderTimePicker(morningTime, setMorningTime)}
              <input
                type="checkbox"
                checked={morningEnabled}
                onChange={e => setMorningEnabled(e.target.checked)}
                className="rounded text-emerald-600 w-4 h-4 cursor-pointer"
              />
            </div>
          </div>

          {/* Late */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60">
            <div className="space-y-0.5">
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <ExclamationTriangleIcon className="w-4 h-4 text-rose-500" />
                بررسی تاخیر ورود
              </span>
              <p className="text-[11px] text-slate-400">هشدار در صورت عدم ثبت ورود</p>
            </div>
            <div className="flex items-center gap-3">
              {renderTimePicker(lateTime, setLateTime)}
              <input
                type="checkbox"
                checked={lateEnabled}
                onChange={e => setLateEnabled(e.target.checked)}
                className="rounded text-emerald-600 w-4 h-4 cursor-pointer"
              />
            </div>
          </div>

          {/* Exit */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60">
            <div className="space-y-0.5">
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <ClockIcon className="w-4 h-4 text-emerald-500" />
                یادآوری پایان روز و خروج
              </span>
              <p className="text-[11px] text-slate-400">ارسال پیام یادآوری ثبت خروج</p>
            </div>
            <div className="flex items-center gap-3">
              {renderTimePicker(exitTime, setExitTime)}
              <input
                type="checkbox"
                checked={exitEnabled}
                onChange={e => setExitEnabled(e.target.checked)}
                className="rounded text-emerald-600 w-4 h-4 cursor-pointer"
              />
            </div>
          </div>

          {/* Report */}
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700/60">
            <div className="space-y-0.5">
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                گزارش اول ماه
              </span>
              <p className="text-[11px] text-slate-400">ارسال اعلان آماده شدن کارنامه ماه</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400 font-semibold">روز ۱ ماه (۰۹:۰۰)</span>
              <input
                type="checkbox"
                checked={reportEnabled}
                onChange={e => setReportEnabled(e.target.checked)}
                className="rounded text-emerald-600 w-4 h-4 cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Notifications History List */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-sm border border-slate-100 dark:border-slate-700/60 space-y-4">
        <h2 className="text-base font-bold text-slate-800 dark:text-white pb-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
          <BellIcon className="w-5 h-5 text-blue-600" />
          تاریخچه پیام‌ها و اعلانات دریافتی
        </h2>

        {notifications.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">
            پیام یا اعلانی ثبت نشده است.
          </p>
        ) : (
          <div className="space-y-2.5">
            {notifications.map(n => (
              <div
                key={n.id}
                onClick={() => !n.read && handleMarkAsRead(n.id)}
                className={`p-4 rounded-2xl border transition cursor-pointer flex items-start justify-between gap-3 ${
                  n.read
                    ? 'bg-slate-50/50 dark:bg-slate-900/20 border-slate-100 dark:border-slate-800/60 opacity-70'
                    : 'bg-white dark:bg-slate-900/60 border-blue-200 dark:border-blue-900/50 shadow-sm'
                }`}
              >
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 whitespace-pre-line leading-relaxed">
                    {n.message}
                  </p>
                  <span className="text-[10px] text-slate-400">
                    {n.created_at ? n.created_at.substring(0, 16).replace('T', ' ') : ''}
                  </span>
                </div>

                {!n.read && (
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0 mt-1" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default Notifications
